import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createAdminUser } from '../../services/api';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { UserPlus, ArrowLeft, Eye, EyeOff } from 'lucide-react'; // Importar ícones Eye e EyeOff
import Breadcrumb from '../../components/Breadcrumb';

function CreateUserPage() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        senha: '',
        instituicao: '',
        grauAcademico: '', // NOVO CAMPO
        role: 'ROLE_PARTICIPANTE', // Default role
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false); // Novo estado para visibilidade da senha

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleRoleChange = (value) => {
        setFormData(prev => ({ ...prev, role: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        if (!formData.nome || !formData.email || !formData.senha || !formData.role) {
            setError('Por favor, preencha todos os campos obrigatórios (Nome, Email, Senha, Papel).');
            setIsLoading(false);
            return;
        }

        try {
            const payload = {
                nome: formData.nome,
                email: formData.email,
                senha: formData.senha,
                instituicao: formData.instituicao,
                grauAcademico: formData.grauAcademico, // INCLUÍDO
                role: formData.role,
            };
            await createAdminUser(payload);
            toast.success(`Usuário ${formData.nome} (${formData.role}) criado com sucesso!`);
            setFormData({
                nome: '',
                email: '',
                senha: '',
                instituicao: '',
                grauAcademico: '',
                role: 'ROLE_PARTICIPANTE',
            });
        } catch (err) {
            console.error('Erro ao criar usuário:', err);
            setError(err.message || 'Ocorreu um erro ao tentar criar o usuário.');
            toast.error(err.message || 'Erro ao criar usuário.');
        } finally {
            setIsLoading(false);
        }
    };

    const breadcrumbItems = [
        { label: 'Dashboard', to: '/dashboard' },
        { label: 'Gerenciar Usuários', to: '/admin/usuarios' },
        { label: 'Criar Usuário' }
    ];

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 mb-6">
                <Button variant="outline" onClick={() => navigate('/admin/usuarios')} className="w-full sm:w-auto">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Voltar à Lista de Usuários
                </Button>
                <h1 className="text-3xl font-bold tracking-tight text-center sm:text-left w-full sm:w-auto">Criar Novo Usuário</h1>
            </div>

            <Card className="w-full max-w-2xl mx-auto">
                <CardHeader>
                    <CardTitle className="flex items-center mb-2"><UserPlus className="mr-2 h-5 w-5" /> Criar Novo Usuário</CardTitle> {/* Adicionado mb-2 */}
                    <CardDescription className="leading-relaxed">
                        Preencha os detalhes para criar uma nova conta de participante ou administrador.
                    </CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent className="grid gap-4">
                        {error && <p className="text-sm font-medium text-destructive">{error}</p>}

                        <div className="grid gap-2">
                            <Label htmlFor="nome" className="mb-1">Nome Completo</Label> {/* Adicionado mb-1 */}
                            <Input
                                id="nome"
                                name="nome"
                                type="text"
                                placeholder="Nome do Usuário"
                                required
                                value={formData.nome}
                                onChange={handleChange}
                                disabled={isLoading}
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="email" className="mb-1">Email</Label> {/* Adicionado mb-1 */}
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                placeholder="usuario@exemplo.com"
                                required
                                value={formData.email}
                                onChange={handleChange}
                                disabled={isLoading}
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="grauAcademico" className="mb-1">Grau Acadêmico (Opcional)</Label> {/* NOVO CAMPO */}
                            <Input
                                id="grauAcademico"
                                name="grauAcademico"
                                type="text"
                                placeholder="Ex: Licenciatura, Mestrado"
                                value={formData.grauAcademico}
                                onChange={handleChange}
                                disabled={isLoading}
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="instituicao" className="mb-1">Instituição (Opcional)</Label> {/* Adicionado mb-1 */}
                            <Input
                                id="instituicao"
                                name="instituicao"
                                type="text"
                                placeholder="Nome da Instituição"
                                value={formData.instituicao}
                                onChange={handleChange}
                                disabled={isLoading}
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="senha" className="mb-1">Senha</Label> {/* Adicionado mb-1 */}
                            <div className="relative"> {/* Adicionado div para posicionamento do ícone */}
                                <Input
                                    id="senha"
                                    name="senha"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Defina uma senha"
                                    required
                                    value={formData.senha}
                                    onChange={handleChange}
                                    disabled={isLoading}
                                    className="pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-primary"
                                    disabled={isLoading}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="role" className="mb-1">Papel</Label> {/* Adicionado mb-1 */}
                            <Select onValueChange={handleRoleChange} value={formData.role} disabled={isLoading}>
                                <SelectTrigger id="role">
                                    <SelectValue placeholder="Selecione o Papel" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="ROLE_PARTICIPANTE">Participante</SelectItem>
                                    <SelectItem value="ROLE_ADMIN">Administrador</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                    <CardFooter>
                        <Button className="w-full" type="submit" disabled={isLoading}>
                            {isLoading ? 'A criar...' : 'Criar Usuário'}
                        </Button>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
}

export default CreateUserPage;